import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type AuthModalMode = "login" | "register" | "forgotPassword";

/** What to do once the visitor signs in from the modal (e.g. Buy Now → open the cart). */
export type AfterLoginAction = "openCart" | null;

interface UiState {
  authModal: {
    open: boolean;
    mode: AuthModalMode;
    afterLogin: AfterLoginAction;
    /** Opened from inside the cart drawer — bring the drawer back when the modal closes. */
    returnToCart: boolean;
  };
  wishlistModalOpen: boolean;
  cartDrawerOpen: boolean;
}

const initialState: UiState = {
  authModal: { open: false, mode: "login", afterLogin: null, returnToCart: false },
  wishlistModalOpen: false,
  cartDrawerOpen: false,
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    openAuthModal: (
      state,
      action: PayloadAction<{ mode?: AuthModalMode; afterLogin?: AfterLoginAction } | undefined>,
    ) => {
      state.authModal.open = true;
      state.authModal.mode = action.payload?.mode ?? "login";
      state.authModal.afterLogin = action.payload?.afterLogin ?? null;
      // The drawer steps aside for the modal, and comes back (remounted, so
      // it re-reads the bag for the new session) once the modal closes.
      state.authModal.returnToCart = state.cartDrawerOpen;
      state.cartDrawerOpen = false;
      state.wishlistModalOpen = false;
    },
    closeAuthModal: (state) => {
      state.authModal.open = false;
      state.authModal.afterLogin = null;
      if (state.authModal.returnToCart) state.cartDrawerOpen = true;
      state.authModal.returnToCart = false;
    },
    setAuthMode: (state, action: PayloadAction<AuthModalMode>) => {
      state.authModal.mode = action.payload;
    },
    openWishlistModal: (state) => {
      state.wishlistModalOpen = true;
    },
    closeWishlistModal: (state) => {
      state.wishlistModalOpen = false;
    },
    openCartDrawer: (state) => {
      state.cartDrawerOpen = true;
      // The wishlist is a Bootstrap modal (z-index 1050) and the drawer sits
      // below it (1020), so leaving it open would bury the cart behind it —
      // e.g. "Go to Cart" from a wishlist row. The cart takes over.
      state.wishlistModalOpen = false;
    },
    closeCartDrawer: (state) => {
      state.cartDrawerOpen = false;
    },
  },
});

export const {
  openAuthModal,
  closeAuthModal,
  setAuthMode,
  openWishlistModal,
  closeWishlistModal,
  openCartDrawer,
  closeCartDrawer,
} = uiSlice.actions;

export default uiSlice.reducer;
